import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import { Link } from "react-router-dom";
import numeral from "numeral";

import { palette, typography, shadows } from "../../theme/brand";
import Button from "./Button.jsx";
import IconsServices from "../common/IconsServices";
import { STATICS_URL } from "../../config/constants";
import vecLogo from "../../assets/img/Header-Logo.png";

// Accommodation list-item card, shared by AccommodationsPage's catalog and
// filtered-results sections (previously ~100 lines duplicated between them).
// Replaces the template `Card` / `CardHeader` / `Button` trio.
const styles = {
  card: {
    display: "flex",
    flexWrap: "wrap",
    backgroundColor: palette.surface,
    borderRadius: "16px",
    boxShadow: shadows.card,
    overflow: "hidden",
    marginBottom: "24px",
    transition: "box-shadow 0.25s ease",
    "&:hover": {
      boxShadow: shadows.cardHover
    }
  },
  imageWrap: {
    flex: "0 0 100%",
    maxWidth: "100%",
    "@media (min-width: 700px)": {
      flex: "0 0 33.333%",
      maxWidth: "33.333%"
    }
  },
  image: {
    display: "block",
    width: "100%",
    height: "210px",
    objectFit: "cover"
  },
  info: {
    flex: "1 1 auto",
    minWidth: 0,
    padding: "20px 24px"
  },
  title: {
    color: palette.textPrimary,
    fontFamily: typography.fontFamilyDisplay,
    fontWeight: 700,
    fontSize: "1.3rem",
    margin: "0 0 4px",
    display: "block",
    textDecoration: "none"
  },
  location: {
    color: palette.textSecondary,
    fontFamily: typography.fontFamilyBase,
    fontSize: "0.9rem",
    margin: "0 0 10px"
  },
  services: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 10px",
    display: "flex",
    flexWrap: "wrap"
  },
  guests: {
    color: palette.textSecondary,
    fontFamily: typography.fontFamilyBase,
    fontSize: "0.85rem",
    margin: 0
  },
  priceBlock: {
    flex: "0 0 100%",
    maxWidth: "100%",
    padding: "0 24px 20px",
    textAlign: "right",
    "@media (min-width: 700px)": {
      flex: "1 1 0",
      maxWidth: "220px",
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-end"
    }
  },
  priceLabel: {
    color: palette.textSecondary,
    fontSize: "0.85rem",
    display: "block"
  },
  price: {
    color: palette.textPrimary,
    fontFamily: typography.fontFamilyDisplay,
    fontWeight: 700,
    fontSize: "1.5rem",
    margin: "2px 0",
    display: "block"
  },
  priceUnit: {
    color: palette.textSecondary,
    fontSize: "0.8rem",
    display: "block",
    marginBottom: "12px"
  }
};

function lowSeasonPrice(accommodation) {
  if (accommodation.pricing && accommodation.pricing.low_season) {
    return accommodation.pricing.low_season;
  }
  if (
    accommodation.rooms &&
    accommodation.rooms.length > 0 &&
    accommodation.rooms[0].pricing &&
    accommodation.rooms[0].pricing.low_season
  ) {
    return accommodation.rooms[0].pricing.low_season;
  }
  return null;
}

function AccommodationCard({ classes, accommodation }) {
  const detailUrl = `/accommodation/${accommodation._id}`;
  const photo =
    accommodation.photos.length > 0
      ? STATICS_URL + accommodation.photos[0].path
      : vecLogo;
  const price = lowSeasonPrice(accommodation);

  return (
    <div className={classes.card}>
      <div className={classes.imageWrap}>
        <Link to={detailUrl}>
          <img className={classes.image} src={photo} alt={accommodation.name} />
        </Link>
      </div>

      <div className={classes.info}>
        <Link to={detailUrl} className={classes.title}>
          {accommodation.name}
        </Link>
        <p className={classes.location}>
          {accommodation.location.address}, {accommodation.location.city},{" "}
          {accommodation.location.department}
        </p>
        <ul className={classes.services}>
          {accommodation.services.map((serv, idx) => (
            <IconsServices serv={serv} key={idx} />
          ))}
        </ul>
        <p className={classes.guests}>
          Mínimo de huéspedes: {accommodation.min_persons}
        </p>
      </div>

      <div className={classes.priceBlock}>
        <span className={classes.priceLabel}>Desde</span>
        {price !== null && (
          <span className={classes.price}>
            COP$ {numeral(price).format("0,0")}
          </span>
        )}
        <span className={classes.priceUnit}>
          {accommodation.full_lease
            ? "Alojamiento completo (IVA no incl.)"
            : "Noche por persona (IVA no incl.)"}
        </span>
        <Link to={detailUrl}>
          <Button color="success">Elegir</Button>
        </Link>
      </div>
    </div>
  );
}

AccommodationCard.propTypes = {
  classes: PropTypes.object.isRequired,
  accommodation: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    photos: PropTypes.array.isRequired,
    location: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      department: PropTypes.string
    }).isRequired,
    services: PropTypes.array.isRequired,
    min_persons: PropTypes.number,
    full_lease: PropTypes.bool,
    pricing: PropTypes.object,
    rooms: PropTypes.array
  }).isRequired
};

export default withStyles(styles)(AccommodationCard);



// WEBPACK FOOTER //
// ./src/components/ui/AccommodationCard.jsx